import React, { useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

// Context
import { ContentProvider } from '@context/ContentContext'
import { FirebaseAuthProvider } from '@context/FirebaseAuthContext'

// Analytics
import { trackPageView } from '@lib/analytics'

// Layout Components
import Navigation from '@components/layout/Navigation'
import Footer from '@components/layout/Footer'

// Page Components
import HomePage from '@pages/HomePage'
import AboutPage from '@pages/AboutPage'
import ServicesPage from '@pages/ServicesPage'
import ResourcesPage from '@pages/ResourcesPage'
import CareersPage from '@pages/CareersPage'
import ContactPage from '@pages/ContactPage'
import TermsPage from '@pages/TermsPage'
import PrivacyPage from '@pages/PrivacyPage'
import CertificationHubPage from '@pages/CertificationHubPage'
import CertificationTrackPage from '@pages/CertificationTrackPage'
import CertificationSignInPage from '@pages/CertificationSignInPage'
import CertificationMyResultsPage from '@pages/CertificationMyResultsPage'
import NotFound from '@pages/NotFound'

// Admin Components
import AdminDashboard from '@pages/admin/AdminDashboard'
import AdminLogin from '@pages/admin/AdminLogin'

function getAnalyticsPageName(pathname) {
  if (pathname === '/') return 'Home'
  if (pathname === '/certification') return 'Certification'
  if (pathname === '/certification/global') return 'Certification Global'
  if (pathname === '/certification/india') return 'Certification India'
  if (pathname === '/certification/sign-in') return 'Certification Sign In'
  if (pathname === '/certification/my-results') return 'Certification My Results'
  const segment = pathname.replace(/^\//, '').split('/')[0]
  if (!segment) return 'Home'
  return segment.charAt(0).toUpperCase() + segment.slice(1)
}

// Scroll to top component with analytics
const ScrollToTop = () => {
  const location = useLocation()

  useEffect(() => {
    // Always scroll to top first when route changes
    window.scrollTo(0, 0)
    
    // Track page view
    const pageName = getAnalyticsPageName(location.pathname)
    trackPageView(pageName)
    
    // If there's a hash, scroll to that element after the page loads
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.substring(1))
        if (element) {
          const navbarHeight = 56 // Account for reduced navbar height (14 * 4 = 56px)
          const elementPosition = element.offsetTop - navbarHeight
          window.scrollTo({ 
            top: elementPosition, 
            behavior: 'smooth' 
          })
        }
      }, 500) // Delay to ensure page is fully loaded
    }
  }, [location.pathname, location.hash])

  return null
}

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20
  },
  in: {
    opacity: 1,
    y: 0
  },
  out: {
    opacity: 0,
    y: -20
  }
}

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.4
}

function App() {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')

  return (
    <FirebaseAuthProvider>
    <ContentProvider>
      <div className="min-h-screen bg-black">
        <ScrollToTop />
        
        {/* Only render Navigation for non-admin routes */}
        {!isAdminRoute && <Navigation />}
        
        <main className="relative">
          <AnimatePresence mode="wait">
            <Routes>
              <Route 
                path="/" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <HomePage />
                  </motion.div>
                } 
              />
              <Route 
                path="/about" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <AboutPage />
                  </motion.div>
                } 
              />
              <Route 
                path="/services" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <ServicesPage />
                  </motion.div>
                } 
              />
              <Route 
                path="/resources" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <ResourcesPage />
                  </motion.div>
                } 
              />
              <Route 
                path="/careers" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <CareersPage />
                  </motion.div>
                } 
              />
              <Route 
                path="/contact" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <ContactPage />
                  </motion.div>
                } 
              />
              <Route 
                path="/terms" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <TermsPage />
                  </motion.div>
                } 
              />
              <Route 
                path="/privacy" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <PrivacyPage />
                  </motion.div>
                } 
              />
              <Route
                path="/certification"
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <CertificationHubPage />
                  </motion.div>
                }
              />
              <Route
                path="/certification/sign-in"
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <CertificationSignInPage />
                  </motion.div>
                }
              />
              <Route
                path="/certification/my-results"
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <CertificationMyResultsPage />
                  </motion.div>
                }
              />
              <Route
                path="/certification/:trackId"
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <CertificationTrackPage />
                  </motion.div>
                }
              />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/*" element={<AdminDashboard />} />
              
              {/* 404 Catch-all Route */}
              <Route 
                path="*" 
                element={
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <NotFound />
                  </motion.div>
                } 
              />
            </Routes>
          </AnimatePresence>
        </main>
        
        {/* Only render Footer for non-admin routes */}
        {!isAdminRoute && <Footer />}
      </div>
    </ContentProvider>
    </FirebaseAuthProvider>
  )
}

export default App 